import assertNever from '@watershed/shared-util/src/assertNever';
import { UnexpectedError } from '@watershed/errors/UnexpectedError';
import { RowExprType } from './lscript/row_types';

export function singleQuote(s: string): string {
  return "'" + singleEscaped(s) + "'";
}

export function singleEscaped(s: string): string {
  return s.replaceAll(/'/g, "''");
}

export function doubleQuote(s: string): string {
  return '"' + s.replaceAll(/"/g, '""') + '"';
}

type ArrayablePrimitive = string | number | boolean;
type NonArrayPrimitive = ArrayablePrimitive | Date | null;
export type SqlPrimitive = NonArrayPrimitive | Array<ArrayablePrimitive>;

export function arrayIncludes<K extends ArrayablePrimitive>(
  value: K,
  array: Array<K>
): boolean {
  return array.includes(value);
}

export function valueToSql(v: SqlPrimitive, exprType?: RowExprType): string {
  switch (typeof v) {
    case 'string': {
      return nonArrayValueToSql(v, exprType);
    }
    case 'number': {
      return nonArrayValueToSql(v, exprType);
    }
    case 'boolean': {
      return nonArrayValueToSql(v, exprType);
    }
    case 'object': {
      if (!v || v instanceof Date) {
        return nonArrayValueToSql(v, exprType);
      } else if (Array.isArray(v)) {
        // eslint-disable-next-line @watershed/no-join-commas
        return `(ARRAY[${v
          .map((val) => nonArrayValueToSql(val, exprType))
          .join(',')}])`;
      } else {
        throw new UnexpectedError(
          `valueToSql should be called only on sql primitives or arrays of sql primitives where allowed, received: ${JSON.stringify(
            v
          )}`
        );
      }
    }
  }
}

function nonArrayValueToSql(
  v: NonArrayPrimitive,
  exprType?: RowExprType
): string {
  if (v === null) {
    if (!exprType) {
      return 'NULL';
    } else if (exprType?.ty === 'string') {
      return 'NULL::VARCHAR';
    } else {
      return 'NULL::INTEGER';
    }
  }

  if (v instanceof Date) {
    try {
      return 'DATE ' + singleQuote(v.toISOString().slice(0, 10));
    } catch (err) {
      throw new UnexpectedError(
        `failed to convert date to sql: ${v.toISOString()}`
      );
    }
  }

  switch (typeof v) {
    case 'string':
      return singleQuote(v);
    case 'number':
      return v.toString();
    case 'boolean':
      return v ? 'TRUE' : 'FALSE';
    default:
      assertNever(v);
  }
}

// Special mapToCase value that means "pass the key value through"
export const PASSTHROUGH = Symbol('mapToCase passthrough');

export function mapToCaseExprs<K extends SqlPrimitive, V extends string>(
  m: ReadonlyMap<K, V>,
  keyExpr: string,
  default_?: V | typeof PASSTHROUGH
): string {
  if (m.size === 0) {
    // CASE expressions can't have zero WHEN clauses
    if (default_ === PASSTHROUGH) {
      return keyExpr;
    }
    return valueToSql(default_ ?? null);
  }

  let out = `CASE ${keyExpr}`;
  for (const [k, v] of m) {
    out += ` WHEN ${valueToSql(k)} THEN ${v}`;
  }
  if (default_ === PASSTHROUGH) {
    out += ` ELSE ${keyExpr}`;
  } else if (default_ !== undefined) {
    out += ` ELSE ${valueToSql(default_)}`;
  }
  return out + ' END';
}

export function mapToCase<K extends SqlPrimitive, V extends SqlPrimitive>(
  m: ReadonlyMap<K, V>,
  keyExpr: string,
  default_?: V | typeof PASSTHROUGH
): string {
  const exprMap = new Map();
  for (const [k, v] of m) {
    exprMap.set(k, valueToSql(v));
  }
  return mapToCaseExprs(exprMap, keyExpr, default_);
}
